import React, { useEffect, useRef, useState } from 'react'
import { IonButton, IonCheckbox, IonContent, IonFooter, IonInput, IonModal, IonRow, IonSelect, IonSelectOption, IonSpinner, IonToolbar, useIonToast } from '@ionic/react'
import { ModalTitle } from 'presentation/components'
import { add, pencil } from 'ionicons/icons'
import { AddGenomicVariantParams } from 'domain/usecases/add-genomic-variant'
import { ReportsService } from 'infra/services/reports-service'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { GenomicVariants } from 'domain/usecases/get-report'

type ModalCreateGenomicVariantProps = React.ComponentPropsWithoutRef<typeof IonModal> & {
  reportId: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  reportsService: ReportsService
  onDismiss: () => void
  onDidDismiss: () => void
  genomicVariant: GenomicVariants | null
}

const resetValues: z.infer<typeof formSchema> = {
  name: '',
  alteration: '',
  actionability: '',
  vaf_type: 'vaf',
  variant_allele_frequency: '',
  variant_allele_frequency_value_from_previous_report: '',
  amplification_status: '',
  suspected_germline: false,
  transcript_id: '',
  coding_sequence_effect: '',
}

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  alteration: z.string().min(1, { message: 'Alteration is required' }),
  actionability: z.string().min(1, { message: 'Actionability is required' }),
  vaf_type: z.enum(['vaf', 'amplification']),
  variant_allele_frequency: z.string().nullable(),
  variant_allele_frequency_value_from_previous_report: z.string().nullable(),
  amplification_status: z.string().nullable(),
  suspected_germline: z.boolean(),
  transcript_id: z.string().nullable(),
  coding_sequence_effect: z.string().nullable(),
})


const ModalCreateGenomicVariant: React.FC<ModalCreateGenomicVariantProps> = (props) => {
  const vafType: 'vaf' | 'amplification' = props.genomicVariant?.amplification_status ? 'amplification' : 'vaf'
  const [type, setType] = useState<'vaf' | 'amplification'>(vafType)
  const [isSaving, setSaving] = useState(false)
  const [presentToast] = useIonToast()
  const modal = useRef<HTMLIonModalElement>(null)

  const { register, handleSubmit, formState: { errors }, reset, setValue, getValues } = useForm({
    resolver: zodResolver(formSchema)
  })

  const save = (data: z.infer<typeof formSchema>) => {
    const { vaf_type, ...rest } = data
    const variantData = {
      report_id: props.reportId,
      genomic_variant: {
        ...rest,
        id: props.genomicVariant?.id?.toString(),
        variant_allele_frequency: data.variant_allele_frequency || '',
        variant_allele_frequency_value_from_previous_report: data.variant_allele_frequency_value_from_previous_report || '',
        type: props.genomicVariant?.type || 'potentially_actionable',
        transcript_id: data.transcript_id || '',
        coding_sequence_effect: data.coding_sequence_effect || '',
      }
    }

    if(!props.genomicVariant) {
      createGenomicVariant(variantData)
    } else {
      updateGenomicVariant(variantData)
    }
  }

  const createGenomicVariant = (data: AddGenomicVariantParams) => {
    setSaving(true)
    props.reportsService.addGenomicVariant(data)
      .then(() => {
        modal.current?.dismiss({ wasEdited: true })
      })
      .catch((message) => {
        presentToast({
          message,
          duration: 5000,
          color: 'danger',
        })
      })
      .finally(() => setSaving(false))
  }

  const updateGenomicVariant = (data: AddGenomicVariantParams) => {
    setSaving(true)
    props.reportsService.updateGenomicVariant(data)
      .then(() => {
        modal.current?.dismiss({ wasEdited: true })
      })
      .catch((message) => {
        presentToast({
          message,
          duration: 5000,
        })
      })
      .finally(() => setSaving(false))
  }

  useEffect(() => {
    if (props.genomicVariant) {
      const vafType: 'vaf' | 'amplification' = props.genomicVariant?.amplification_status ? 'amplification' : 'vaf'
      reset({
        ...props.genomicVariant, vaf_type: vafType,
        actionability: props.genomicVariant?.actionability || '',
        variant_allele_frequency: props.genomicVariant?.variant_allele_frequency || '',
        variant_allele_frequency_value_from_previous_report: props.genomicVariant?.variant_allele_frequency_value_from_previous_report || '',
        amplification_status: props.genomicVariant?.amplification_status || '',
        suspected_germline: props.genomicVariant?.suspected_germline || false,
      })
    } else {
      reset({ ...resetValues })
    }
  }, [props.genomicVariant])

  return (
    <IonModal
      className="modal-create-genomic-variant"
      ref={modal}
      isOpen={props.isOpen}
      onWillDismiss={() => {
        props.onDismiss()
      }}
      onDidDismiss={(info) => {
        setType('vaf')
        reset({ ...resetValues })
        if (info.detail.data && info.detail.data.wasEdited) {
          props.setIsOpen(false)
          props.onDidDismiss()
        }
      }}
    >
      <IonContent className="ion-padding modal-edit-report-header">
        <ModalTitle icon={props.genomicVariant ? pencil : add} label={props.genomicVariant ? 'Edit Genomic Variant' : 'Add Genomic Variant'} />
        <form id="create-genomic-variant-form" onSubmit={handleSubmit(save)}>
          <div className={errors.name ? 'error-field free-text-input' : 'free-text-input'}>
            <span className="label ion-align-items-center">Variant Name <span className="required">*</span></span>
            <IonInput
              placeholder="-"
              required
              {...register('name')}
            />
            {errors.name && <small className="error">{errors.name.message}</small>}
          </div>
          <div className={errors.alteration ? 'error-field free-text-input' : 'free-text-input'}>
            <span className="label ion-align-items-center">Alteration <span className="required">*</span></span>
            <IonInput
              placeholder="-"
              required
              {...register('alteration')}
            />
            {errors.alteration && <small className="error">{errors.alteration.message}</small>}
          </div>
          <div className={errors.actionability ? 'error-field free-text-input' : 'free-text-input'}>
            <span className="label ion-align-items-center">Actionability <span className="required">*</span></span>
            <IonSelect
              placeholder="-"
              required
              {...register('actionability')}
              interface="popover"
            >
              <IonSelectOption value="High">High</IonSelectOption>
              <IonSelectOption value="Medium">Medium</IonSelectOption>
              <IonSelectOption value="Low">Low</IonSelectOption>
            </IonSelect>
            {errors.actionability && <small className="error">{errors.actionability.message}</small>}
          </div>
          <div className={errors.transcript_id ? 'error-field free-text-input' : 'free-text-input'}>
            <span className="label ion-align-items-center">Transcript ID</span>
            <IonInput
              placeholder="-"
              required
              {...register('transcript_id')}
            />
          </div>
          <div className={errors.coding_sequence_effect ? 'error-field free-text-input' : 'free-text-input'}>
            <span className="label ion-align-items-center">Coding Sequence Effect</span>
            <IonInput
              placeholder="-"
              required
              {...register('coding_sequence_effect')}
            />
          </div>

          <div className={'free-text-input'}>
            <span className="label ion-align-items-center">Type</span>
            <IonSelect
              value={type}
              onIonChange={(e) => {
                setType(e.target.value as 'vaf' | 'amplification')
                if (e.target.value === 'vaf') {
                  setValue('amplification_status', '')
                } else {
                  setValue('variant_allele_frequency', '')
                  setValue('variant_allele_frequency_value_from_previous_report', '')
                }
                register('vaf_type').onChange(e)
              }}
              {...register('vaf_type')}
            >
              <IonSelectOption value="vaf">VAF</IonSelectOption>
              <IonSelectOption value="amplification">Amplification</IonSelectOption>
            </IonSelect>
          </div>
          {type === 'vaf' && (
            <>
              <div className={errors.variant_allele_frequency ? 'error-field free-text-input' : 'free-text-input'}>
                <span className="label ion-align-items-center">Variant Allele Frequency</span>
                <IonInput
                  placeholder="-"
                  required
                  {...register('variant_allele_frequency')}
                />
                {errors.variant_allele_frequency && <small className="error">{errors.variant_allele_frequency.message}</small>}
              </div>
              <div className={errors.variant_allele_frequency_value_from_previous_report ? 'error-field free-text-input' : 'free-text-input'}>
                <span className="label ion-align-items-center">Variant Allele Frequency Value from Previous Report</span>
                <IonInput
                  placeholder="-"
                  required
                  {...register('variant_allele_frequency_value_from_previous_report')}
                />
                {errors.variant_allele_frequency_value_from_previous_report && <small className="error">{errors.variant_allele_frequency_value_from_previous_report.message}</small>}
              </div>
            </>
          )}
          {type === 'amplification' && (
            <>
              <div className={errors.amplification_status ? 'error-field free-text-input' : 'free-text-input'}>
                <span className="label ion-align-items-center">Amplification Status</span>
                <IonInput
                  placeholder="-"
                  required
                  {...register('amplification_status')}
                />
                {errors.amplification_status && <small className="error">{errors.amplification_status.message}</small>}
              </div>
            </>
          )}
          <IonRow className="ion-align-items-center free-checkbox-input">
            <label htmlFor="suspected-germline">Suspected Germline</label>
            <IonCheckbox
              id="suspected-germline"
              {...register('suspected_germline')}
              checked={getValues('suspected_germline')}
              onIonChange={(e) => {
                setValue('suspected_germline', e.target.checked)
              }}
            ></IonCheckbox>
          </IonRow>
        </form>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButton slot="end" fill="outline" onClick={() => {
            props.setIsOpen(false)
            reset()
          }}>
            Cancel
          </IonButton>

          <IonButton
            slot="end"
            fill="solid"
            disabled={isSaving}
            onClick={handleSubmit(save)}
            form="create-genomic-variant-form"
          >
            {isSaving ? <IonSpinner /> : 'Save'}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  )
}

export default ModalCreateGenomicVariant

